/**
 * Client-side push subscription state.
 *
 * Lives outside `usePushNotifications` so `authStore` can clear it on sign-out
 * without importing the hook (which imports `authStore` in turn).
 */

/** Device token handed to us by APNs/FCM via Capacitor. */
const NATIVE_TOKEN_KEY = "push_device_token";

/**
 * Stable per-install id. The backend keys native rows on this rather than the
 * device token, which the OS rotates on reinstall and restore-from-backup.
 *
 * Stored in localStorage, not `@capacitor/preferences` — its native calls never
 * resolve in the Android WebView. See the note in `lib/supabase.ts`.
 */
const INSTALL_ID_KEY = "push_install_id";

/** Last time we re-sent our subscription to the backend. */
const RESYNC_AT_KEY = "push_resync_at";
const RESYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * The user turned push off in settings. OS permission stays granted, so without
 * this the foreground re-register would silently switch it back on.
 */
const OPTED_OUT_KEY = "push_opted_out";

export function getCachedPushToken(): string | null {
  return localStorage.getItem(NATIVE_TOKEN_KEY);
}

export function setCachedPushToken(token: string): void {
  localStorage.setItem(NATIVE_TOKEN_KEY, token);
}

/**
 * Drop the cached device token on sign-out. `install_id` deliberately survives
 * — it identifies the install, not the account, and the backend keys rows on
 * `(user_id, install_id)`.
 */
export function clearCachedPushToken(): void {
  localStorage.removeItem(NATIVE_TOKEN_KEY);
  localStorage.removeItem(RESYNC_AT_KEY);
}

export function isOptedOut(): boolean {
  return localStorage.getItem(OPTED_OUT_KEY) === "1";
}

export function setOptedOut(optedOut: boolean): void {
  if (optedOut) localStorage.setItem(OPTED_OUT_KEY, "1");
  else localStorage.removeItem(OPTED_OUT_KEY);
}

/**
 * Re-sending a subscription is idempotent and cheap, but it doesn't need to
 * happen on every foreground.
 */
export function shouldResync(): boolean {
  const last = Number(localStorage.getItem(RESYNC_AT_KEY) ?? 0);
  return !Number.isFinite(last) || Date.now() - last > RESYNC_INTERVAL_MS;
}

export function markResynced(): void {
  localStorage.setItem(RESYNC_AT_KEY, String(Date.now()));
}

/** Generated once per install and persisted; survives token rotation. */
export function getInstallId(): string {
  const existing = localStorage.getItem(INSTALL_ID_KEY);
  if (existing) return existing;

  const installId = crypto.randomUUID();
  localStorage.setItem(INSTALL_ID_KEY, installId);
  return installId;
}
